/**
 * Alluvion Home — Print Your Own Photo Upload & Cart
 * ===================================================
 * Handles photo upload to Cloudflare R2 and cart add with photo URL
 * as a line item property. Dawn handles all variant selection natively.
 *
 * Only runs on: /products/print-your-own-photo
 *
 * INSTALLATION
 * ─────────────────────────────────────────
 * STEP 1 — Add this file as a theme asset
 *   Shopify Admin → Online Store → Themes → Edit code
 *   Assets → Add a new asset → name it alluvion-configurator.js → paste this file
 *
 * STEP 2 — Load the script in layout/theme.liquid
 *   Find where your existing script tags are and update to:
 *
 *   {% if request.path contains '/products/print-your-own-photo' %}
 *     <script src="{{ 'alluvion-preview.js' | asset_url }}" defer></script>
 *     <script src="{{ 'alluvion-configurator.js' | asset_url }}" defer></script>
 *   {% endif %}
 *
 * STEP 3 — Add upload zone in sections/main-product.liquid
 *   Find the price block and paste directly after it:
 *
 *   {% if product.handle == 'print-your-own-photo' %}
 *     <div id="alv-upload-section">
 *       <p class="alv-label">Your photo</p>
 *       <div class="alv-upload-zone" id="alv-upload-zone">
 *         <input type="file" id="alv-file-input" accept="image/*">
 *         <div class="alv-upload-idle" id="alv-upload-idle">
 *           <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="7" y2="8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
 *           <span>Click or drag to upload your photo</span>
 *           <span class="alv-upload-sub">JPG, PNG or TIFF &middot; min 1500px on shortest side</span>
 *         </div>
 *         <div class="alv-upload-overlay" id="alv-upload-overlay" style="display:none;">
 *           <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="7" y2="8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
 *           <span>Replace photo</span>
 *         </div>
 *         <button class="alv-remove-btn" id="alv-remove-btn" type="button" style="display:none;">&#x2715; Remove</button>
 *       </div>
 *       <p class="alv-res-warning" id="alv-res-warning" style="display:none;">
 *         &#9888; Your image may be too low resolution for this size and could print with reduced quality.
 *       </p>
 *     </div>
 *   {% endif %}
 *
 *   Also add this near the closing </div> of the product info section:
 *
 *   <script type="application/json" id="alv-product-json">
 *     {{ product | json }}
 *   </script>
 */

(function () {
  'use strict';

  /* ── Guard ────────────────────────────────────────────────────────── */
  if (!window.location.pathname.includes('/products/print-your-own-photo')) return;

  /* ── Cloudflare Worker URL ────────────────────────────────────────── */
  var WORKER_URL = 'https://alluvion-upload.elliot-389.workers.dev';

  /* ── Minimum pixels on shortest side per print size ──────────────── */
  var MIN_PX = { A5: 874, A4: 1240, A3: 1748, A2: 2480, A1: 3508, A0: 4964 };

  /* ── State ────────────────────────────────────────────────────────── */
  var productData  = null;
  var imgW = 0, imgH = 0;
  var photoReady   = false;
  var photoFile    = null;
  var photoUrl     = null;
  var isUploading  = false;

  /* ── Boot ─────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    var jsonEl = document.getElementById('alv-product-json');
    if (jsonEl) {
      try { productData = JSON.parse(jsonEl.textContent); } catch (e) {
        console.warn('[Alluvion] Could not parse product JSON.', e);
      }
    }

    injectStyles();
    bindUploadEvents();
    interceptAddToCart();
    bindSizeChange();
  });

  /* ── Watch Dawn's native Size dropdown for resolution warnings ────── */
  function bindSizeChange() {
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (el && el.name && el.name.indexOf('Finished Size') !== -1) {
        checkResolution(el.value);
      }
    });
  }

  /* ── Get currently selected finished size from Dawn's dropdown ───── */
  function getSelectedSize() {
    var select = document.querySelector('select[name="options[Finished Size]"]');
    return select ? select.value : null;
  }

  /* ── Get currently selected framing from Dawn's dropdown ──────────── */
  function getSelectedFraming() {
    var select = document.querySelector('select[name="options[Framing]"]');
    return select ? select.value : null;
  }

  /* ── Find the matching Shopify variant ───────────────────────────── */
  function findVariant() {
    if (!productData) return null;
    var size    = getSelectedSize();
    var framing = getSelectedFraming();
    if (!size || !framing) return null;

    return productData.variants.find(function (v) {
      return productData.options.every(function (name, idx) {
        var key = 'option' + (idx + 1);
        if (name === 'Finished Size') return v[key] === size;
        if (name === 'Framing')       return v[key] === framing;
        return true;
      });
    }) || null;
  }

  /* ── Intercept Add to Cart — upload photo first ──────────────────── */
  function interceptAddToCart() {
    var atcBtn = document.querySelector('.product-form__submit');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', function (e) {
      /* No photo selected — let Dawn handle normally */
      if (!photoFile || isUploading) return;

      /* Already uploaded this session — let Dawn handle normally */
      if (photoUrl) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      isUploading = true;
      var originalText = atcBtn.textContent;
      atcBtn.disabled  = true;
      atcBtn.textContent = 'Uploading photo… 0%';

      var variant = findVariant();
      if (!variant) {
        alert('Please select a size and framing option before adding to cart.');
        atcBtn.disabled    = false;
        atcBtn.textContent = originalText;
        isUploading        = false;
        return;
      }

      /* Upload photo to Cloudflare Worker */
      var formData = new FormData();
      formData.append('file', photoFile);

      var xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', function (event) {
        if (event.lengthComputable) {
          var percent = Math.round((event.loaded / event.total) * 100);
          atcBtn.textContent = 'Uploading photo… ' + percent + '%';
        }
      });

      xhr.addEventListener('load', function () {
        if (xhr.status < 200 || xhr.status >= 300) {
          try {
            var errorData = JSON.parse(xhr.responseText);
            throw new Error(errorData.error || 'Upload failed');
          } catch (e) {
            throw new Error('Upload failed');
          }
        }

        try {
          var data = JSON.parse(xhr.responseText);

          if (!data.url) {
            throw new Error(data.error || 'No URL returned');
          }

          photoUrl = data.url;

          atcBtn.textContent = 'Adding to cart…';

          /* Get the exact sections Dawn's cart drawer expects */
          var cartDrawer = document.querySelector('cart-drawer');
          var sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
            ? cartDrawer.getSectionsToRender().map(function (section) {
                return section.id;
              })
            : ['cart-drawer', 'cart-icon-bubble'];

          /* Add to cart via Shopify AJAX API with photo as line item property */
          fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id:         variant.id,
                quantity:   1,
                properties: {
  'Photo': photoUrl,
  '_Filename': photoFile ? photoFile.name : 'Uploaded photo'
}
              }],
              sections: sections,
              sections_url: window.location.pathname
            })
          })
          .then(function (res) { return res.json(); })
          .then(function (cartResponse) {
            isUploading        = false;
            atcBtn.disabled    = false;
            atcBtn.textContent = originalText;
            photoUrl           = null;

            /* Render Dawn's cart drawer with the fresh cart contents */
            var cartDrawer = document.querySelector('cart-drawer');

            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.classList.remove('is-empty');
              cartDrawer.renderContents(cartResponse);
            } else {
              /* Fallback: open Dawn's cart drawer normally */
              var cartIcon = document.querySelector('.header__icon--cart');
              if (cartIcon) {
                cartIcon.click();
              }
            }
          })
          .catch(function (err) {
            console.error('[Alluvion] Error:', err);
            alert('Sorry, there was a problem. Please try again.');
            isUploading        = false;
            atcBtn.disabled    = false;
            atcBtn.textContent = originalText;
            photoUrl           = null;
          });

        } catch (err) {
          console.error('[Alluvion] Error:', err);
          alert('Sorry, there was a problem. Please try again.');
          isUploading        = false;
          atcBtn.disabled    = false;
          atcBtn.textContent = originalText;
          photoUrl           = null;
        }
      });

      xhr.addEventListener('error', function () {
        console.error('[Alluvion] Upload error');
        alert('Sorry, there was a problem uploading your photo. Please try again.');
        isUploading        = false;
        atcBtn.disabled    = false;
        atcBtn.textContent = originalText;
        photoUrl           = null;
      });

      xhr.addEventListener('abort', function () {
        console.error('[Alluvion] Upload aborted');
        isUploading        = false;
        atcBtn.disabled    = false;
        atcBtn.textContent = originalText;
        photoUrl           = null;
      });

      xhr.open('POST', WORKER_URL, true);
      xhr.send(formData);
    }, true);
  }

  /* ── Photo upload handlers ───────────────────────────────────────── */
  function bindUploadEvents() {
    var fileInput = document.getElementById('alv-file-input');
    var zone      = document.getElementById('alv-upload-zone');
    var removeBtn = document.getElementById('alv-remove-btn');
    if (!fileInput || !zone) return;

    zone.addEventListener('click', function (e) {
      if (removeBtn && (e.target === removeBtn || removeBtn.contains(e.target))) return;
      fileInput.click();
    });

    fileInput.addEventListener('change', function (e) {
      if (e.target.files[0]) handleFile(e.target.files[0]);
    });

    zone.addEventListener('dragover', function (e) {
      e.preventDefault();
      zone.classList.add('alv-upload-zone--dragover');
    });
    zone.addEventListener('dragleave', function () {
      zone.classList.remove('alv-upload-zone--dragover');
    });
    zone.addEventListener('drop', function (e) {
      e.preventDefault();
      zone.classList.remove('alv-upload-zone--dragover');
      var file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) handleFile(file);
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        clearPhoto();
      });
    }
  }

  function handleFile(file) {
    photoFile = file;
    photoUrl  = null;
    var reader = new FileReader();
    reader.onload = function (ev) {
      var img = new Image();
      img.onload = function () {
        imgW = img.naturalWidth;
        imgH = img.naturalHeight;
        showPreview(ev.target.result);
        photoReady = true;
        checkResolution(getSelectedSize());

        /* Notify preview script */
        var orientation = imgW >= imgH ? 'landscape' : 'portrait';
        document.dispatchEvent(new CustomEvent('alv:photo-changed', {
          detail: { dataUrl: ev.target.result, orientation: orientation }
        }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  function showPreview(src) {
    var zone    = document.getElementById('alv-upload-zone');
    var idle    = document.getElementById('alv-upload-idle');
    var overlay = document.getElementById('alv-upload-overlay');
    var remove  = document.getElementById('alv-remove-btn');
    if (!zone) return;

    var preview = zone.querySelector('.alv-preview');
    if (!preview) {
      preview     = document.createElement('img');
      preview.className = 'alv-preview';
      preview.alt = 'Your uploaded photo preview';
      zone.insertBefore(preview, zone.firstChild);
    }
    preview.src = src;
    zone.classList.add('alv-upload-zone--has-image');
    if (idle)    idle.style.display    = 'none';
    if (overlay) overlay.style.display = 'flex';
    if (remove)  remove.style.display  = 'inline-block';
  }

  function clearPhoto() {
    var zone    = document.getElementById('alv-upload-zone');
    var idle    = document.getElementById('alv-upload-idle');
    var overlay = document.getElementById('alv-upload-overlay');
    var remove  = document.getElementById('alv-remove-btn');
    var input   = document.getElementById('alv-file-input');
    if (!zone) return;

    var preview = zone.querySelector('.alv-preview');
    if (preview) preview.remove();
    zone.classList.remove('alv-upload-zone--has-image');
    if (idle)    idle.style.display    = 'flex';
    if (overlay) overlay.style.display = 'none';
    if (remove)  remove.style.display  = 'none';
    if (input)   input.value           = '';
    photoReady = false;
    photoFile  = null;
    photoUrl   = null;
    imgW = imgH = 0;

    document.dispatchEvent(new CustomEvent('alv:photo-cleared'));
  }

  function checkResolution(size) {
    var warn = document.getElementById('alv-res-warning');
    if (!warn) return;
    if (!photoReady || !size || !MIN_PX[size]) { warn.style.display = 'none'; return; }
    warn.style.display = Math.min(imgW, imgH) < MIN_PX[size] ? 'block' : 'none';
  }

  /* ── Inject scoped CSS ────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById('alv-styles')) return;
    var style = document.createElement('style');
    style.id  = 'alv-styles';
    style.textContent = [
      '#alv-upload-section { margin-bottom: 2.4rem; }',
      '.alv-label { font-size: 1.3rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; margin: 0 0 .8rem; color: rgb(var(--color-foreground)); }',
      '.alv-upload-zone { position: relative; overflow: hidden; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 180px; border: 1px dashed rgba(var(--color-foreground),.3); border-radius: var(--buttons-radius,4px); background: rgba(var(--color-foreground),.03); cursor: pointer; transition: background .15s, border-color .15s; }',
      '.alv-upload-zone:hover, .alv-upload-zone--dragover { background: rgba(var(--color-foreground),.07); border-color: rgba(var(--color-foreground),.55); }',
      '#alv-file-input { display: none; }',
      '.alv-upload-idle { display: flex; flex-direction: column; align-items: center; gap: .5rem; color: rgba(var(--color-foreground),.55); font-size: 1.3rem; pointer-events: none; text-align: center; padding: 0 1rem; }',
      '.alv-upload-sub { font-size: 1.1rem; color: rgba(var(--color-foreground),.38); }',
      '.alv-preview { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }',
      '.alv-upload-overlay { position: absolute; inset: 0; background: rgba(0,0,0,.45); flex-direction: column; align-items: center; justify-content: center; gap: .5rem; color: #fff; font-size: 1.3rem; cursor: pointer; }',
      '.alv-upload-zone--has-image:hover .alv-upload-overlay { display: flex !important; }',
      '.alv-remove-btn { position: absolute; top: 8px; right: 8px; z-index: 2; background: rgb(var(--color-background)); border: 1px solid rgba(var(--color-foreground),.2); border-radius: var(--buttons-radius,4px); padding: 4px 10px; font-size: 1.1rem; font-family: inherit; cursor: pointer; color: rgb(var(--color-foreground)); }',
      '.alv-res-warning { margin-top: .6rem; font-size: 1.2rem; color: #b45309; }',
    ].join('\n');
    document.head.appendChild(style);
  }

})();